import type { GameMetadata, GameModule } from '@wp/game-core';
import CATALOGUE from 'virtual:wp-catalogue';
import { gameCatalogueMessages } from './i18n';

export interface GameEntry {
  /**
   * Catalogue metadata bundled with the shell: everything from the game's metadata, but only the messages the
   * shell renders before the game is loaded (title, tagline, rules, difficulty labels; see catalogue-plugin.ts).
   * `messages` holds only the locales loaded so far (`loadLocale` in ./i18n: the UI locale and English).
   * The complete metadata is `(await load()).metadata`.
   */
  metadata: GameMetadata;
  /** Lazily loads the game code, so each game is its own chunk and can be launched directly. */
  load: () => Promise<GameModule<unknown>>;
}

const entry = <S>(id: string, load: () => Promise<{ default: GameModule<S> }>): GameEntry => {
  const metadata = (CATALOGUE as Record<string, Omit<GameMetadata, 'messages'> | undefined>)[id];
  if (!metadata) throw new Error(`Game "${id}" is missing from the catalogue (add @wp/game-${id} to apps/web/package.json).`);
  return { metadata: { ...metadata, messages: gameCatalogueMessages(id) }, load: async () => (await load()).default as GameModule<unknown> };
};

/**
 * Catalogue order = order of this list (owner, 2026-10-14). Adding a game: one line here + a package under packages/games.
 * 1. The large games first: strategy (Relay Command), the Lootlings card game (owner, 2026-10-17), strategy/tower hybrid, Tower Defense, the point-and-click adventures
 *    (each joins this group when it ships).
 * 2. Then the small games in the owner's order: Nonogram, Orbit Links, Chess, Mine Logic, Letter Logic, Stack Duel,
 *    Skyscrapers, Lights Out, Crate Pusher, Laser Paths, Circuit, Memory; then the rest.
 */
export const GAMES: readonly GameEntry[] = [
  entry('relay-command', () => import('@wp/game-relay-command')),
  entry('card-quest', () => import('@wp/game-card-quest')),
  entry('nonogram', () => import('@wp/game-nonogram')),
  // Owner (2026-10-31): Tower Defense at place 4, the strategy/TD hybrid at place 7 of the overall list.
  entry('tower-defense', () => import('@wp/game-tower-defense')),
  entry('node-conquest', () => import('@wp/game-node-conquest')),
  entry('pixel-peel', () => import('@wp/game-pixel-peel')),
  entry('hold-the-line', () => import('@wp/game-hold-the-line')),
  entry('chess', () => import('@wp/game-chess')),
  entry('minesweeper', () => import('@wp/game-minesweeper')),
  entry('word-guess', () => import('@wp/game-word-guess')),
  entry('stack-duel', () => import('@wp/game-stack-duel')),
  entry('skyscrapers', () => import('@wp/game-skyscrapers')),
  entry('lights-out', () => import('@wp/game-lights-out')),
  entry('sokoban', () => import('@wp/game-sokoban')),
  entry('laser-circuit', () => import('@wp/game-laser-circuit')),
  entry('circuit-puzzle', () => import('@wp/game-circuit-puzzle')),
  entry('memory', () => import('@wp/game-memory')),
  entry('poker', () => import('@wp/game-poker')),
  entry('skat', () => import('@wp/game-skat')),
  entry('mastermind', () => import('@wp/game-mastermind')),
  entry('black-box', () => import('@wp/game-black-box')),
  entry('rule-discovery', () => import('@wp/game-rule-discovery')),
  entry('graph-detective', () => import('@wp/game-graph-detective')),
  entry('logic-path', () => import('@wp/game-logic-path')),
  entry('systems-puzzle', () => import('@wp/game-systems-puzzle')),
  entry('debug-system', () => import('@wp/game-debug-system')),
  entry('constraint-grid', () => import('@wp/game-constraint-grid')),
  entry('minimal-proof', () => import('@wp/game-minimal-proof')),
  entry('bridges', () => import('@wp/game-bridges')),
  entry('sliding-blocks', () => import('@wp/game-sliding-blocks')),
  entry('river-crossing', () => import('@wp/game-river-crossing')),
  entry('review', () => import('@wp/game-review')),
  entry('sequence-memory', () => import('@wp/game-sequence-memory')),
  entry('spatial-memory', () => import('@wp/game-spatial-memory')),
  entry('signal-watch', () => import('@wp/game-signal-watch')),
  entry('distractor-control', () => import('@wp/game-distractor-control')),
  entry('connect-four', () => import('@wp/game-connect-four')),
  entry('tic-tac-toe', () => import('@wp/game-tic-tac-toe')),
  entry('slitherlink', () => import('@wp/game-slitherlink')),
  entry('n-back', () => import('@wp/game-n-back')),
  entry('visual-search', () => import('@wp/game-visual-search')),
  entry('prospective-memory', () => import('@wp/game-prospective-memory')),
  entry('association', () => import('@wp/game-association')),
  entry('ambiguity-detector', () => import('@wp/game-ambiguity-detector')),
  entry('audience-switch', () => import('@wp/game-audience-switch')),
  entry('deep-read', () => import('@wp/game-deep-read')),
  entry('faces-names', () => import('@wp/game-faces-names')),
  entry('briefing-game', () => import('@wp/game-briefing-game')),
  entry('compression-challenge', () => import('@wp/game-compression-challenge')),
  entry('mini-optimizer', () => import('@wp/game-mini-optimizer')),
  entry('fermi-lab', () => import('@wp/game-fermi-lab')),
  entry('many-lives', () => import('@wp/game-many-lives')),
  entry('probability-detective', () => import('@wp/game-probability-detective')),
  entry('atlas-explorer', () => import('@wp/game-atlas-explorer')),
  entry('timeline', () => import('@wp/game-timeline')),
  entry('ecosystem', () => import('@wp/game-ecosystem')),
  entry('harmony', () => import('@wp/game-harmony')),
  entry('balance-lab', () => import('@wp/game-balance-lab'))
];

export const findGame = (id: string): GameEntry | undefined => GAMES.find((g) => g.metadata.id === id);
